/**
 * Small pure helpers for the House Guide (Touch Stay style, docs/GUEST-REDESIGN.md
 * Section 5). A guide page is one Markdown body in the reader's language
 * (already split by bodyFor in bilingual.ts). These two functions read that
 * plain text without any rendering, so they are cheap to test.
 */

/** One accordion section: a heading (H2 text) and everything under it, as Markdown. */
export type GuideSection = {
  heading: string;
  body: string;
};

/**
 * Splits a page body on its H2 headings ("## Heading"). A heading at level 3
 * or deeper stays inside the section above it, so "How the island works"
 * becomes one section per topic (hot water, air conditioning, and so on)
 * rather than one section per sub point.
 *
 * A body with no H2 headings returns a single section with an empty heading,
 * so a caller can always ask "how many sections" without a special case for
 * plain pages.
 */
export function splitH2Sections(markdown: string): GuideSection[] {
  const lines = markdown.split(/\r?\n/);
  const sections: GuideSection[] = [];
  let heading = '';
  let body: string[] = [];
  let started = false;

  for (const line of lines) {
    const match = /^##\s+(.*)$/.exec(line);
    if (match) {
      if (started) sections.push({ heading, body: body.join('\n').trim() });
      heading = (match[1] ?? '').trim();
      body = [];
      started = true;
      continue;
    }
    if (!started) {
      // Text before the first H2. Keep it as an unheaded section so nothing
      // written above the first heading is silently dropped.
      started = true;
    }
    body.push(line);
  }
  if (started) sections.push({ heading, body: body.join('\n').trim() });

  return sections.filter((s) => s.heading || s.body);
}

/**
 * The first paragraph of a page body, for a short mention elsewhere (the
 * "Where you sleep" card on the House Guide overview, which links through to
 * the full Who sleeps where screen rather than repeating everything here).
 */
export function firstParagraph(markdown: string): string {
  const blocks = markdown.trim().split(/\r?\n\s*\r?\n/);
  const first = blocks.find((b) => b.trim() && !/^#{1,4}\s/.test(b.trim()));
  return (first ?? '').replace(/\s+/g, ' ').trim();
}
